import * as React from 'react';
import { useState } from 'react';
import { Plus } from 'lucide-react';
import {
  SEGMENT_FIELD_REGISTRY,
  segmentFieldEnum,
  type SegmentDefinition,
  type SegmentGroup,
  type SegmentLogicalOp,
  type SegmentCondition,
} from '@masters-club/validation';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { SegmentGroupEditor } from './SegmentGroupEditor';
import { SegmentConditionEditor } from './SegmentConditionEditor';
import { SegmentPresets } from './SegmentPresets';
import { logicalOpLabel } from './segment-labels';

export interface SegmentBuilderProps {
  value: SegmentDefinition;
  onChange: (next: SegmentDefinition) => void;
  disabled?: boolean;
}

function createDefaultGroup(): SegmentGroup {
  const firstField = segmentFieldEnum.options[0];
  const meta = SEGMENT_FIELD_REGISTRY[firstField];
  return {
    conditionsOp: 'AND',
    conditions: [
      {
        field: firstField,
        operator: meta.operators[0],
        value: undefined,
      },
    ],
  };
}

function createDefaultCondition(): SegmentCondition {
  const firstField = segmentFieldEnum.options[0];
  const meta = SEGMENT_FIELD_REGISTRY[firstField];
  return {
    field: firstField,
    operator: meta.operators[0],
    value: undefined,
  };
}

/**
 * Управляемый конструктор сегмента аудитории.
 *
 * Два режима:
 *   Простой (по умолчанию) — плоский список условий одной группы.
 *   Расширенный — двухуровневое дерево с группами и AND/OR.
 */
export function SegmentBuilder({
  value,
  onChange,
  disabled = false,
}: SegmentBuilderProps) {
  const [isAdvanced, setIsAdvanced] = useState(() => value.groups.length > 1);

  const handleGroupsOpChange = (op: SegmentLogicalOp) => {
    onChange({ ...value, groupsOp: op });
  };

  const handleGroupChange = (index: number, next: SegmentGroup) => {
    const groups = value.groups.map((g, i) => (i === index ? next : g));
    onChange({ ...value, groups });
  };

  const handleGroupRemove = (index: number) => {
    const groups = value.groups.filter((_, i) => i !== index);
    onChange({ ...value, groups });
  };

  const handleAddGroup = () => {
    onChange({
      ...value,
      groups: [...value.groups, createDefaultGroup()],
    });
  };

  // Простой режим: операции на первой группе (groups[0])
  const handleSimpleConditionChange = (index: number, next: SegmentCondition) => {
    const group = value.groups[0];
    if (!group) { return; }
    const conditions = group.conditions.map((c, i) => (i === index ? next : c));
    onChange({ ...value, groups: [{ ...group, conditions }] });
  };

  const handleSimpleConditionRemove = (index: number) => {
    const group = value.groups[0];
    if (!group) { return; }
    const conditions = group.conditions.filter((_, i) => i !== index);
    onChange({ ...value, groups: [{ ...group, conditions }] });
  };

  const handleSimpleAddCondition = () => {
    if (value.groups.length === 0) {
      // Создаём первую группу
      onChange({
        ...value,
        groups: [{ conditionsOp: 'AND', conditions: [createDefaultCondition()] }],
      });
    } else {
      const group = value.groups[0];
      onChange({
        ...value,
        groups: [{ ...group, conditions: [...group.conditions, createDefaultCondition()] }],
      });
    }
  };

  const handleToggleAdvanced = (advanced: boolean) => {
    setIsAdvanced(advanced);
  };

  const handlePresetApply = (group: SegmentGroup) => {
    if (!isAdvanced) {
      // В простом режиме добавляем условия из пресета в первую группу
      const existingGroup = value.groups[0];
      if (!existingGroup) {
        onChange({ ...value, groups: [group] });
      } else {
        onChange({
          ...value,
          groups: [{ ...existingGroup, conditions: [...existingGroup.conditions, ...group.conditions] }],
        });
      }
    } else {
      // В расширенном режиме добавляем группу целиком
      onChange({ ...value, groups: [...value.groups, group] });
    }
  };

  const simpleGroup = value.groups[0];
  const simpleConditions = simpleGroup?.conditions ?? [];

  return (
    <div className="space-y-4">
      {/* Быстрые пресеты */}
      <div className="space-y-1.5">
        <p className="text-xs text-muted-foreground">Быстрые сценарии:</p>
        <SegmentPresets onApply={handlePresetApply} disabled={disabled} />
      </div>

      {/* Toggle режима */}
      <div className="flex items-center gap-2">
        <Switch
          id="segment-advanced-mode"
          checked={isAdvanced}
          onCheckedChange={handleToggleAdvanced}
          disabled={disabled}
        />
        <Label htmlFor="segment-advanced-mode" className="text-sm text-muted-foreground cursor-pointer">
          Расширенный режим
        </Label>
      </div>

      {!isAdvanced ? (
        /* Простой режим — плоский список условий */
        <div className="space-y-3">
          {simpleConditions.length === 0 && (
            <div className="rounded-md border border-dashed p-6 text-center">
              <p className="text-sm text-muted-foreground">
                Нет условий — будет выбрана вся аудитория.
              </p>
            </div>
          )}
          <div className="space-y-2">
            {simpleConditions.map((condition, i) => (
              <React.Fragment key={i}>
                {i > 0 && (
                  <div className="flex items-center gap-2 py-1">
                    <Separator className="flex-1" />
                    <span className="text-xs font-medium text-muted-foreground px-2">
                      {logicalOpLabel(simpleGroup?.conditionsOp ?? 'AND')}
                    </span>
                    <Separator className="flex-1" />
                  </div>
                )}
                <SegmentConditionEditor
                  condition={condition}
                  onChange={(next) => handleSimpleConditionChange(i, next)}
                  onRemove={() => handleSimpleConditionRemove(i)}
                  disabled={disabled}
                />
              </React.Fragment>
            ))}
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleSimpleAddCondition}
            disabled={disabled || simpleConditions.length >= 50}
            className="w-full border-dashed"
          >
            <Plus className="h-4 w-4 mr-1" />
            Добавить условие
          </Button>
        </div>
      ) : (
        /* Расширенный режим — двухуровневое дерево групп */
        <div className="space-y-4">
          {value.groups.length === 0 && (
            <div className="rounded-md border border-dashed p-6 text-center">
              <p className="text-sm text-muted-foreground">
                Нет условий — будет выбрана вся аудитория.
              </p>
            </div>
          )}

          {value.groups.length > 1 && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">Группы объединены через:</span>
              <div className="flex rounded-md border overflow-hidden text-xs">
                {(['AND', 'OR'] as SegmentLogicalOp[]).map((op) => (
                  <button
                    key={op}
                    type="button"
                    disabled={disabled}
                    onClick={() => handleGroupsOpChange(op)}
                    className={`px-3 py-1 transition-colors ${
                      value.groupsOp === op
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-background text-muted-foreground hover:bg-muted'
                    }`}
                  >
                    {logicalOpLabel(op)}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="space-y-3">
            {value.groups.map((group, i) => (
              <React.Fragment key={i}>
                {i > 0 && (
                  <div className="flex items-center gap-2 py-1">
                    <Separator className="flex-1" />
                    <span className="text-xs font-semibold text-muted-foreground px-2">
                      {logicalOpLabel(value.groupsOp)}
                    </span>
                    <Separator className="flex-1" />
                  </div>
                )}
                <SegmentGroupEditor
                  group={group}
                  groupIndex={i}
                  onChange={(next) => handleGroupChange(i, next)}
                  onRemove={() => handleGroupRemove(i)}
                  disabled={disabled}
                />
              </React.Fragment>
            ))}
          </div>

          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleAddGroup}
            disabled={disabled || value.groups.length >= 20}
            className="w-full border-dashed"
          >
            <Plus className="h-4 w-4 mr-1" />
            Добавить группу условий
          </Button>
        </div>
      )}
    </div>
  );
}
